// A staff sandbox (sculpt-api PLAN-sandbox.md): on the left the conversation the user had, turn by turn, each one
// playable again; on the right the sandbox's own conversation as it plays, the model deciding anew and the server's
// simulator standing in for Excel. Staff can type their own messages between the recorded ones, answer what a turn
// stops on (a question card, calls to confirm), and read each played turn beside what it did for the user. Nothing here
// touches the user's conversation or workbook; opening the page files an audit row on the API side.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { dateTime, usd } from '../../services/format';
import type { AgentMode, SandboxComparison, SandboxDetail as Detail, SandboxEvent, SandboxPending } from '../../types/api';
import { Alert, Badge, Button, Card, Checkbox, Confirm, Input, KeyValue, PageTitle, Select, Textarea } from '../../components/ui';
import { SandboxPane } from './SandboxPane';
import { replay, formatDuration } from './replay';
import { stateTone } from './common';

const MODES: AgentMode[] = ['plan', 'ask', 'auto', 'full_auto'];
const POLL_MS = 1500;
/** The API's default page of events (`GET /admin/sandboxes/{id}/events`, `limit` 500). */
const EVENTS_PAGE = 500;

/** A question card the turn stopped on: each question with the agent's suggestion pre-filled. */
function QuestionForm({ pending, busy, onAnswer }: { pending: SandboxPending; busy: boolean; onAnswer: (answers: unknown[]) => void }) {
  const { t } = useTranslation();
  const questions = pending.batch.calls.filter((c) => c.tool_name === 'ask_user')
    .flatMap((c) => ((c.input as { questions?: { id: string; text: string; kind: string; options?: { value?: string; label?: string }[]; suggestion?: string }[] }).questions ?? []));
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(questions.map((q) => [q.id, q.suggestion ?? ''])));
  return (
    <div className="flex flex-col gap-2" data-testid="question-form">
      {questions.map((q) => (
        <label key={q.id} className="flex flex-col gap-1 text-[12.5px]">
          <span className="text-ink">{q.text}</span>
          {q.kind === 'choice' && q.options?.length ? (
            <Select value={values[q.id] ?? ''} onChange={(e) => setValues({ ...values, [q.id]: e.target.value })}>
              {q.options.map((o, i) => (
                <option key={i} value={o.value ?? o.label ?? ''}>{o.label ?? o.value}</option>
              ))}
            </Select>
          ) : (
            <Input value={values[q.id] ?? ''} onChange={(e) => setValues({ ...values, [q.id]: e.target.value })} />
          )}
        </label>
      ))}
      <div>
        <Button variant="primary" busy={busy} data-testid="answer-question"
                onClick={() => onAnswer(questions.map((q) => ({ id: q.id, answer: values[q.id] ?? '', left_to_sculpt: !values[q.id] })))}>
          {t('sandbox.answer')}
        </Button>
      </div>
    </div>
  );
}

function ComparisonCard({ rows }: { rows: SandboxComparison[] }) {
  const { t } = useTranslation();
  if (!rows.length) return null;
  const plans = (s: SandboxComparison['recorded']) => s.plans.map((p) => `${p.name ?? '—'} (${p.calls ?? '?'})`).join(', ') || '—';
  return (
    <Card title={t('sandbox.comparison')}>
      <div className="flex flex-col gap-3" data-testid="comparison">
        {rows.map((r) => (
          <div key={r.sandbox_turn} className="flex flex-col gap-1 border-b border-line-soft pb-2 text-[12px] last:border-0">
            <div className="flex flex-wrap items-center gap-1">
              <span className="font-mono text-[10.5px] uppercase text-faint">{t('sandbox.comparedTurns', { recorded: r.recorded_turn, sandbox: r.sandbox_turn })}</span>
              <Badge tone={r.same_tools ? 'good' : 'warn'}>{t(r.same_tools ? 'sandbox.sameTools' : 'sandbox.otherTools')}</Badge>
              <Badge tone={r.same_plans ? 'good' : 'warn'}>{t(r.same_plans ? 'sandbox.samePlans' : 'sandbox.otherPlans')}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-2 font-mono text-[11px]">
              <div><span className="text-faint">{t('sandbox.recorded')}: </span>{r.recorded.tools.join(' → ') || '—'}<br />{plans(r.recorded)}</div>
              <div><span className="text-faint">{t('sandbox.sandbox')}: </span>{r.sandbox.tools.join(' → ') || '—'}<br />{plans(r.sandbox)}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function SandboxDetail() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data, error, reload } = useAsync(() => api.sandbox(id), [id]);
  const [events, setEvents] = useState<SandboxEvent[]>([]);
  const after = useRef(0);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [mode, setMode] = useState<AgentMode>('auto');
  const [deleting, setDeleting] = useState(false);
  const live = data?.state === 'running';

  // The events of the sandbox, then, while a turn runs, every POLL_MS; the page reloads once the turn stops.
  useEffect(() => {
    let stopped = false;
    const poll = async () => {
      try {
        // every page since the last event read: the API answers at most EVENTS_PAGE at a time
        for (;;) {
          const fresh = await api.sandboxEvents(id, after.current);
          if (stopped || !fresh.length) return;
          after.current = fresh[fresh.length - 1]!.id;
          setEvents((old) => [...old, ...fresh]);
          if (fresh.some((e) => ['turn_done', 'waiting', 'failed', 'cancelled'].includes(e.event))) reload();
          if (fresh.length < EVENTS_PAGE) return;
        }
      } catch {
        /* a missed poll is retried by the next one */
      }
    };
    void poll();
    if (!live) return () => { stopped = true; };
    const timer = window.setInterval(() => void poll(), POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [id, live, reload]);

  const played = useMemo(() => replay(events), [events]);

  const act = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    try {
      await work();
      reload();
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Alert>{error}</Alert>;
  if (!data) return null;
  const d: Detail = data;
  const userTimes = new Map(d.source_turns.filter((x) => x.duration_ms != null).map((x) => [x.turn_index, x.duration_ms as number]));
  const blocked = d.state === 'running' || d.state === 'waiting';
  const next = d.source_turns.find((x) => x.turn_index === d.next_turn);

  return (
    <div>
      <PageTitle
        title={t('sandbox.title')}
        subtitle={
          <span>
            <Link className="underline" to={`/conversations/${d.source_thread_id}`}>{d.source_title ?? d.source_thread_id}</Link>
            {' · '}<Badge tone={stateTone(d.state)}>{t(`sandbox.states.${d.state}`)}</Badge>
          </span>
        }
        actions={
          <>
            {blocked && <Button onClick={() => void act(() => api.cancelSandbox(id))} data-testid="cancel-sandbox">{t('sandbox.cancel')}</Button>}
            <Button variant="ghost" onClick={() => setDeleting(true)} data-testid="delete-sandbox">{t('sandbox.delete')}</Button>
          </>
        }
      />
      {actionError && <div className="mb-3"><Alert>{actionError}</Alert></div>}
      {d.error && <div className="mb-3"><Alert>{d.error}</Alert></div>}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <div className="flex min-w-0 flex-col gap-3">
          <Card title={t('sandbox.about')}>
            <KeyValue rows={[
              ...(d.reason ? [[t('sandbox.reason'), d.reason] as [string, string]] : []),
              [t('sandbox.startedAt'), t('sandbox.startedAtValue', { turn: d.from_turn, version: d.source_version })],
              [t('sandbox.model'), [d.model, d.effort].filter(Boolean).join(' ')],
              [t('sandbox.approveAll'), (
                <Checkbox key="approve" label={t('sandbox.approveAllLabel')} checked={d.approve_all} disabled={busy} data-testid="approve-all"
                          onChange={(e) => void act(() => api.updateSandbox(id, { approve_all: e.target.checked }))} />
              )],
              [t('sandbox.cost'), usd(d.cost_usd, i18n.language)],
              [t('sandbox.staff'), d.staff_email ?? '—'],
              [t('sandbox.expires'), dateTime(d.expires_at, i18n.language)],
            ]} />
          </Card>
          <Card title={t('sandbox.recordedTurns')}>
            <div className="flex flex-col gap-2" data-testid="source-turns">
              {d.source_turns.map((x) => (
                <div key={x.turn_index} className={`flex items-start justify-between gap-2 rounded-sm px-2 py-1.5 ${x.turn_index === d.next_turn ? 'bg-accent-bg' : ''}`}
                     data-testid={`source-turn-${x.turn_index}`}>
                  <div className="min-w-0 text-[12.5px]">
                    <div className="font-mono text-[10.5px] uppercase text-faint">
                      {t('quality.turn', { index: x.turn_index })}{x.mode ? ` · ${x.mode}` : ''}{x.index_version !== null ? ` · v${x.index_version}` : ''}
                      {x.documents > 0 ? ` · ${t('sandbox.documents', { count: x.documents })}` : ''}
                      {x.duration_ms != null ? ` · ${formatDuration(x.duration_ms)}` : ''}
                    </div>
                    <p className="whitespace-pre-wrap wrap-break-word text-ink">{x.message}</p>
                  </div>
                  <Button disabled={blocked || busy || x.turn_index < d.from_turn} onClick={() => void act(() => api.playSandbox(id, x.turn_index))}
                          data-testid={`play-${x.turn_index}`}>{t('sandbox.play')}</Button>
                </div>
              ))}
            </div>
          </Card>
          <ComparisonCard rows={d.comparison} />
        </div>
        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" disabled={blocked || !next} busy={busy} onClick={() => void act(() => api.playSandbox(id))} data-testid="play-next">
              {next ? t('sandbox.playNext', { index: next.turn_index }) : t('sandbox.noNext')}
            </Button>
          </div>
          {d.pending && (
            <Card title={t(d.pending.reason === 'question' ? 'sandbox.waitingQuestion' : 'sandbox.waitingConfirmation')}>
              {d.pending.reason === 'question' ? (
                <QuestionForm pending={d.pending} busy={busy} onAnswer={(answers) => void act(() => api.answerSandbox(id, { approve: true, answers }))} />
              ) : (
                <div className="flex flex-col gap-2" data-testid="confirmation">
                  <ul className="list-disc pl-4 font-mono text-[11.5px]">
                    {d.pending.batch.calls.map((c) => <li key={c.tool_call_id}>{c.tool_name}</li>)}
                  </ul>
                  <div className="flex gap-2">
                    <Button variant="primary" busy={busy} onClick={() => void act(() => api.answerSandbox(id, { approve: true }))} data-testid="approve">{t('sandbox.approve')}</Button>
                    <Button busy={busy} onClick={() => void act(() => api.answerSandbox(id, { approve: false }))} data-testid="refuse">{t('sandbox.refuse')}</Button>
                  </div>
                </div>
              )}
            </Card>
          )}
          <SandboxPane turns={played} userTimes={userTimes} messages={d.messages} />
          <Card title={t('sandbox.yourMessage')}>
            <div className="flex flex-col gap-2">
              <Textarea rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t('sandbox.messagePlaceholder')} data-testid="sandbox-message" />
              <div className="flex items-center gap-2">
                <Select value={mode} onChange={(e) => setMode(e.target.value as AgentMode)} data-testid="sandbox-mode">
                  {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </Select>
                <Button variant="primary" disabled={blocked || !draft.trim()} busy={busy} data-testid="send-message"
                        onClick={() => void act(async () => { await api.sandboxMessage(id, draft.trim(), mode); setDraft(''); })}>
                  {t('sandbox.send')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
      <Confirm open={deleting} title={t('sandbox.deleteTitle')} message={t('sandbox.deleteMessage')} confirmLabel={t('sandbox.delete')} danger busy={busy}
               onCancel={() => setDeleting(false)}
               onConfirm={() => void act(async () => { await api.deleteSandbox(id); navigate('/sandboxes'); })} />
    </div>
  );
}
